import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { isEmptyObject, hasNoQuotes } from '../../utils/apiHelpers';
import { useLocation } from 'react-router-dom';
import SeoTagsManager from '../../utils/SeoTagsManager';

import MobileProviderTileComponent from '../../components/ProviderTile/MobileProviderTileComponent_LifeInsurance';
import ProviderTileComponent from '../../components/ProviderTile/ProviderTileComponent_LifeInsurance';

import FilterPanelComponent from '../../components/FilterPanel/FilterPanelComponent_Dynamic';
import MobileFilterPanelComponent from '../../components/FilterPanel/MobileFilterPanelComponent_Dynamic';
import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import ResultsHeader from '../../components/ResultHeader/ResultHeaderComponent';
import MobileResultsHeader from '../../components/ResultHeader/MobileResultHeaderComponent';
import { useMobileContext } from '../../contexts/MobileContext';

import { getLifeQuote, acceptQuoteBuyOnline } from '../../api/apiServices';
import { withRouter } from '../../utils/withRouter';
import TakeMeHomeButton from '../../components/TakeMeHomeButton/TakeMeHomeButton';

import FilterPanelData from '../../json/components/FilterPanel/FilterPanel_Life.json';

const isSection = (v) =>
  v &&
  typeof v === 'object' &&
  ['radio', 'checkbox', 'slider'].includes(v.type);
const getSections = (schema) =>
  Object.entries(schema).filter(([, v]) => isSection(v));
const buildDefaultsFromSchema = (schema) => {
  const out = {};
  for (const [key, def] of getSections(schema)) {
    if (def.type === 'radio') out[key] = def.defaultSelectedOption ?? '';
    else if (def.type === 'checkbox') out[key] = [];
    else if (def.type === 'slider') out[key] = def.end ?? 0;
  }
  return out;
};
const buildDefaultsFromCurrentConfig = (cfg) => {
  const out = {};
  for (const [key, def] of getSections(cfg)) {
    if (def.type === 'radio') out[key] = def.defaultSelectedOption ?? '';
    else if (def.type === 'checkbox') out[key] = [];
    else if (def.type === 'slider') out[key] = def.end ?? 0;
  }
  return out;
};
const getByPath = (obj, path) => {
  if (!path) return undefined;
  return path
    .split('.')
    .reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
};
const asLower = (v) => (v == null ? '' : String(v).trim().toLowerCase());

// When a quote can be bought online for less, use the online premium as its
// ordering key against other quotes' premiums; otherwise use the total premium.
const getOrderingPremium = (q) =>
  q.OnlinePremium && q.OnlinePremium < q.TotalPremium
    ? q.OnlinePremium
    : q.TotalPremium;

import gsap from 'gsap';

// Fireworks (match Quote page)
import lottie from 'lottie-web';
import fireworksData from '../../assets/lottie/fireworks.json';
import {
  getCrossSellQuotesForResults,
  getCrossSellQuoteDetails,
} from '../../utils/crossSellResultsHelper';
import {
  markProductChosen,
  unmarkProductChosen,
  isProductChosen,
  getLandingPathForFallbackKey,
} from '../../utils/carouselQuoteHelpers';
import { pushQuoteResultsView } from '../../utils/dataLayer';

const FilterIcon = 'https://assets.askhippo.co.za/Icons/common/filter.svg';

/* ---------------------------------------
   Fireworks overlay
   - Burst from BOTH sides of card top
   - Target total duration ~2500ms
   - Render BEHIND the ProviderTile
----------------------------------------*/
const FireworksFromTop = ({ playSignal, isMobile }) => {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const leftLottieRef = useRef(null);
  const rightLottieRef = useRef(null);

  useEffect(() => {
    if (leftRef.current) {
      leftLottieRef.current = lottie.loadAnimation({
        container: leftRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    if (rightRef.current) {
      rightLottieRef.current = lottie.loadAnimation({
        container: rightRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    return () => {
      leftLottieRef.current?.destroy();
      rightLottieRef.current?.destroy();
      leftLottieRef.current = null;
      rightLottieRef.current = null;
    };
  }, []);

  useEffect(() => {
    const playForDurationMs = (instance, desiredMs = 2500) => {
      if (!instance) return;
      const nativeSecs =
        typeof instance.getDuration === 'function'
          ? instance.getDuration(false)
          : null;
      const speed =
        nativeSecs && nativeSecs > 0 ? nativeSecs / (desiredMs / 1000) : 1; // total time ≈ desiredMs
      instance.setSpeed(speed);
      instance.goToAndPlay(0);
    };
    playForDurationMs(leftLottieRef.current, 3500);
    playForDurationMs(rightLottieRef.current, 3500);
  }, [playSignal]);

  // Size: responsive for mobile; roomy on desktop

  function getDeviceScreenSize() {
    if (typeof window === 'undefined') {
      return { width: 0, height: 0 }; // SSR fallback
    }
    return {
      width: window.screen.width,
      height: window.screen.height,
    };
  }

  // Size: responsive for mobile; roomy on desktop
  const { width } = getDeviceScreenSize();
  const FW_SIZE = isMobile ? Math.max(320, Math.round(width * 0.7)) : 520;
  const HALF = FW_SIZE / 2;

  return (
    <>
      {/* Left burst (above top-left) - BEHIND card */}
      <Box
        ref={leftRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          left: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(-35%) rotate(-35deg)',
          pointerEvents: 'none',
          zIndex: 1, // behind the card (card is zIndex:2 in wrapper)
        }}
      />
      {/* Right burst (above top-right) - BEHIND card */}
      <Box
        ref={rightRef}
        sx={{
          position: 'absolute',
          top: `-${HALF}px`,
          right: 0,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'translateX(35%) rotate(35deg)',
          pointerEvents: 'none',
          zIndex: 1, // behind the card
        }}
      />
    </>
  );
};
function ResultPage(props) {
  const { navigate, isMobile, isTablet } = props;
  const INITIAL_TILE_APPEAR_MS = 300; // non-top-3 tiles
  const REVEAL_STAGGER_MS = 500; // top-3 tiles (match initial)
  const BASE_REVEAL_DELAY_MS = 800; // slight pause after modal "Yes"

  // Inside your ResultPage component
  const { category, product, formValues } = props.location?.state || {}; // fallback if not passed
  const isMobileOrTablet = isMobile || isTablet;
  const bestDealRef = useRef(null);
  const introPanelRef = useRef(null);

  const crossSellQuoteDetails = getCrossSellQuoteDetails(props.location?.state);

  // States
  const [loading, setLoading] = useState(
    !sessionStorage.getItem('hippo_reveal_done')
  );
  const [buyNowLoading, setBuyNowLoading] = useState(false);
  const [buyNowLoadingForQuotePk, setBuyNowLoadingForQuotePk] = useState('');
  const [fetching, setFetching] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [bestDealContentReady, setBestDealContentReady] = useState(
    !!sessionStorage.getItem('hippo_reveal_done')
  );
  const [showTeaseModal, setShowTeaseModal] = useState(false);
  const [allowFinalTile, setAllowFinalTile] = useState(false);
  const [hideTop3Permanently, setHideTop3Permanently] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [headerText, setHeaderText] = useState('');
  const [showIntroPanel, setShowIntroPanel] = useState(
    !props.location?.state?.fromCrossSell
  );
  const [introDelayDone, setIntroDelayDone] = useState(
    !!props.location?.state?.fromCrossSell
  );
  const [quotesForAnimation, setQuotesForAnimation] = useState([]);
  const [avgPremium, setAvgPremium] = useState(0);
  const [visibleQuotes, setVisibleQuotes] = useState(0);
  const scaledOnceRef = useRef(new Set());

  // Fireworks control
  const [fireworksSignal, setFireworksSignal] = useState(0);
  const [fireworksArmed, setFireworksArmed] = useState(false);
  const hasSeenRevealRef = useRef(
    sessionStorage.getItem('hippo_reveal_done') === '1'
  );

  const initialCoverValueRaw =
    formValues?.lifeCoverValue ?? crossSellQuoteDetails?.['Cover Value'] ?? '';

  const [displayCoverValue, setDisplayCoverValue] = useState(
    initialCoverValueRaw !== ''
      ? typeof initialCoverValueRaw === 'number'
        ? `R${Math.round(initialCoverValueRaw).toLocaleString('en-ZA')}.00`
        : initialCoverValueRaw
      : ''
  );

  const parsedInitialCoverValue = (() => {
    const cleaned = String(initialCoverValueRaw || '').replace(/[^0-9.]/g, '');
    const num = Math.round(parseFloat(cleaned));
    return !isNaN(num) && num > 0 ? num : 1000000;
  })();

  const [filterConfig, setFilterConfig] = useState(() => ({
    ...FilterPanelData,
    selectedSections: {
      ...buildDefaultsFromSchema(FilterPanelData),
      CoverValue: parsedInitialCoverValue,
    },
  }));
  const [appliedFilters, setAppliedFilters] = useState(() =>
    Object.fromEntries(
      Object.entries(buildDefaultsFromSchema(FilterPanelData)).filter(
        ([k]) => k !== 'CoverValue'
      )
    )
  );
  const [liveFormValues, setLiveFormValues] = useState(() => ({
    ...(formValues || {}),
  }));
  const lastAppliedCoverValueRef = useRef(parsedInitialCoverValue);

  const fetchQuotes = useCallback(async (values) => {
    const crossSellQuotes = getCrossSellQuotesForResults(props.location?.state);
    if (!crossSellQuotes && isEmptyObject(values)) {
      setFetching(false);
      return;
    }

    setFetching(true);

    const quotesFromAPI =
      crossSellQuotes ?? (await getLifeQuote(values).catch(() => []));

    if (hasNoQuotes(quotesFromAPI)) {
      setInitialLoadDone(true);
      setFetching(false);
      return;
    }

    const avgPremiumValue =
      quotesFromAPI.reduce((sum, q) => sum + q.TotalPremium, 0) /
      quotesFromAPI.length;
    setAvgPremium(avgPremiumValue);

    const ranked = [...quotesFromAPI]
      .sort((a, b) => getOrderingPremium(a) - getOrderingPremium(b))
      .map((q, i) => ({ ...q, _rank: i + 1 }));

    const top3Ids = new Set(
      ranked.slice(0, Math.min(3, ranked.length)).map((q) => q.QuotePK)
    );
    const bestId = ranked[0]?.QuotePK;

    const updatedQuotes = ranked.map((quote) => {
      const tags = [];
      const isBestDeal = quote.QuotePK === bestId;
      const isTop3 = top3Ids.has(quote.QuotePK);

      const hasBuyNow = Boolean(quote.OnlinePremium);
      // Only advertise a price when buying online actually costs less.
      const hasOnlineSaving =
        hasBuyNow && quote.OnlinePremium < quote.TotalPremium;
      const buyOnlineChipText = hasOnlineSaving
        ? `Only R${Math.round(quote.OnlinePremium).toLocaleString('en-ZA')}pm when you Buy Online!`
        : 'Available to Buy Online';

      if (hasBuyNow) {
        // Anything buyable online gets the buy-online chip, in preference to
        // the "better than avg" chip even when it is the best deal.
        tags.push({
          text: buyOnlineChipText,
          color: '#E3E800',
        });
      } else if (isBestDeal) {
        const improvement = Math.round(
          ((avgPremiumValue - quote.TotalPremium) / avgPremiumValue) * 100
        );
        tags.push({
          text: `${improvement}% Better than the avg.`,
          color: '#67F4F0',
        });
      }

      return { ...quote, bestDeal: isBestDeal, isTop3, RetailChips: tags };
    });

    setQuotes(updatedQuotes);
    pushQuoteResultsView({ product });
    localStorage.setItem(
      'latestLifeInsuranceQuotes',
      JSON.stringify(updatedQuotes)
    );
    unmarkProductChosen('latestLifeInsuranceQuotes');

    const lowestPremium = Math.min(...quotesFromAPI.map((q) => q.TotalPremium));
    const improvement = Math.round(avgPremiumValue - lowestPremium);
    setHeaderText(
      `Your best price is <b>R${improvement} better</b> than average.`
    );

    setFetching(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isProductChosen('latestLifeInsuranceQuotes')) {
      navigate(getLandingPathForFallbackKey('latestLifeInsuranceQuotes'), {
        replace: true,
      });
      return;
    }
    fetchQuotes(liveFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animate in all except top 3, then show modal (if >5 total) or auto-reveal top 3 (if <=5)
  useEffect(() => {
    if (
      fetching ||
      initialLoadDone ||
      !introDelayDone ||
      quotes.length === 0 ||
      quotesForAnimation.length > 0
    ) {
      return;
    }

    const nonTop3 = quotes.filter((q) => !q.isTop3);

    // If there are nonTop3 quotes, animate them in first
    if (nonTop3.length > 0) {
      const animationOrder = [...nonTop3].sort(
        (a, b) => getOrderingPremium(b) - getOrderingPremium(a)
      );

      let delay = 0;
      const step = INITIAL_TILE_APPEAR_MS;
      const firstGap = Math.round(step * 3);

      animationOrder.forEach((quote, index) => {
        setTimeout(() => {
          setQuotesForAnimation((prev) => [quote, ...prev]);

          // After last nonTop3:
          if (index === animationOrder.length - 1) {
            // If more than 5 quotes total, show modal; otherwise auto-reveal top 3
            if (quotes.length > 5) {
              if (hasSeenRevealRef.current) {
                setTimeout(() => setAllowFinalTile(true), 600);
              } else {
                sessionStorage.setItem('hippo_reveal_done', '1');
                setTimeout(() => setShowTeaseModal(true), 1000);
              }
            } else {
              setTimeout(() => setAllowFinalTile(true), 600);
            }
          }
        }, delay);
        delay += index === 0 ? firstGap : step;
      });
    } else {
      // No nonTop3 means all quotes are within top 3 (i.e., total <=3).
      // We still want a stagger reveal of the top 3 -> auto allow.
      setTimeout(() => setAllowFinalTile(true), 400);
    }
  }, [fetching, initialLoadDone, quotes, quotesForAnimation, introDelayDone]);

  // When user chooses to reveal (or auto-allow for <=5), add top 3 in sequence (#3 -> #2 -> #1)
  useEffect(() => {
    if (!allowFinalTile || quotes.length === 0) return;

    const top3Ascending = [...quotes]
      .filter((q) => q.isTop3)
      .sort((a, b) => getOrderingPremium(a) - getOrderingPremium(b)); // rank 1..3

    const revealOrder = [
      top3Ascending[2],
      top3Ascending[1],
      top3Ascending[0],
    ].filter(Boolean); // [#3(worst of best), #2, #1(best)]

    // If there are zero top3 (e.g., <=3 case but flagged differently), just finalize
    if (revealOrder.length === 0) {
      setInitialLoadDone(true);
      return;
    }

    revealOrder.forEach((q, idx) => {
      setTimeout(
        () => {
          setQuotesForAnimation((prev) => [q, ...prev]);
          if (q?.bestDeal && bestDealRef.current && !initialLoadDone) {
            setTimeout(() => {
              setInitialLoadDone(true);
            }, 300);
          }
        },
        BASE_REVEAL_DELAY_MS + idx * REVEAL_STAGGER_MS
      );
    });
  }, [allowFinalTile, quotes, initialLoadDone]);

  useEffect(() => {
    if (!fetching && !showIntroPanel) {
      setTimeout(() => setIntroDelayDone(true), 2000);
    }
  }, [fetching, showIntroPanel]);

  const ProviderTile = isMobileOrTablet
    ? MobileProviderTileComponent
    : ProviderTileComponent;

  const ResultHeaderComponent = isMobileOrTablet
    ? MobileResultsHeader
    : ResultsHeader;

  useEffect(() => {
    if (hasSeenRevealRef.current) return;
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Finalize initial load after best is revealed (kept for timing polish)
  useEffect(() => {
    if (!loading && bestDealRef.current && allowFinalTile && !initialLoadDone) {
      setTimeout(() => {
        setInitialLoadDone(true);
      }, 1200);
    }
  }, [loading, allowFinalTile, initialLoadDone]);

  const handleApply = async (cfgFromPanel) => {
    const cfg = cfgFromPanel ?? filterConfig;
    setFilterConfig(cfg);

    const ss = cfg?.selectedSections || {};
    const { CoverValue: newCoverValue, ...localFilters } = ss;
    setAppliedFilters(localFilters);

    if (newCoverValue !== lastAppliedCoverValueRef.current) {
      lastAppliedCoverValueRef.current = newCoverValue;

      const formatted = `R${Math.round(newCoverValue).toLocaleString('en-ZA')}.00`;
      setDisplayCoverValue(formatted);
      try {
        const stored = JSON.parse(localStorage.getItem('formValues') || '{}');
        localStorage.setItem(
          'formValues',
          JSON.stringify({ ...stored, lifeCoverValue: formatted })
        );
      } catch (_) {
        /* ignore localStorage errors */
      }

      const next = {
        ...liveFormValues,
        selectedCoverValueOverride: newCoverValue,
      };
      setLiveFormValues(next);

      setQuotes([]);
      setQuotesForAnimation([]);
      setAllowFinalTile(false);
      setHideTop3Permanently(false);
      setInitialLoadDone(false);
      setVisibleQuotes(0);
      scaledOnceRef.current.clear();
      setFireworksArmed(false);

      await fetchQuotes(next);
    }

    setShowMobileFilterPanel(false);
  };

  const handleReset = () => {
    const defaults = buildDefaultsFromCurrentConfig(filterConfig);
    setFilterConfig((cfg) => ({ ...cfg, selectedSections: defaults }));
    setAppliedFilters(
      Object.fromEntries(
        Object.entries(defaults).filter(([k]) => k !== 'CoverValue')
      )
    );
  };

  const filteredQuotes = quotes.filter((quote) => {
    if (hideTop3Permanently && quote.isTop3) return false;
    const selections = appliedFilters || {};
    for (const [pathKey, selected] of Object.entries(selections)) {
      const def = FilterPanelData[pathKey];
      if (!def || !isSection(def)) continue;
      const type = def.type;
      if (type === 'radio') {
        const sv = asLower(selected);
        if (sv === '' || sv === 'all' || sv === 'any') continue;
        if (asLower(getByPath(quote, pathKey)) !== sv) return false;
      }
      if (type === 'checkbox') {
        if (!Array.isArray(selected) || selected.length === 0) continue;
        const qVal = getByPath(quote, pathKey);
        if (qVal === undefined) {
          if (
            !selected.every(
              (fieldKey) => asLower(getByPath(quote, fieldKey)) === 'true'
            )
          )
            return false;
        } else {
          const quoteArr = Array.isArray(qVal) ? qVal : [qVal];
          const selSet = new Set(selected.map(asLower));
          if (!quoteArr.some((v) => selSet.has(asLower(v)))) return false;
        }
      }
      if (type === 'slider') {
        const qNum = Number(getByPath(quote, pathKey));
        const sNum = Number(selected);
        if (Number.isFinite(sNum)) {
          if (!Number.isFinite(qNum) || qNum > sNum) return false;
        }
      }
    }
    return true;
  });

  const [sortOption, setSortOption] = useState('priceLowHigh');

  const sortOptions = [
    { value: 'priceLowHigh', label: 'Price low - high' },
    { value: 'priceHighLow', label: 'Price high - low' },
    { value: 'coverValueLowHigh', label: 'Cover Value low - high' },
    { value: 'coverValueHighLow', label: 'Cover Value high - low' },
  ];

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    switch (sortOption) {
      case 'priceLowHigh':
        return (
          getOrderingPremium(a) - getOrderingPremium(b) || a._rank - b._rank
        );
      case 'priceHighLow':
        return (
          getOrderingPremium(b) - getOrderingPremium(a) || a._rank - b._rank
        );
      case 'coverValueLowHigh':
        return a.CoverValue - b.CoverValue || a._rank - b._rank;
      case 'coverValueHighLow':
        return b.CoverValue - a.CoverValue || a._rank - b._rank;
      default:
        return 0;
    }
  });

  const handleModalButtonClick = (id) => {
    if (id === 'keep-searching') {
      setShowTeaseModal(false);
      setAllowFinalTile(true); // reveal the top 3
    } else if (id === 'stop-searching') {
      setShowTeaseModal(false);
      setHideTop3Permanently(true); // never show the top 3
      setInitialLoadDone(true);
    }
  };

  const [showMobileFilterPanel, setShowMobileFilterPanel] = useState(false);

  const withheldCount = Math.min(3, quotes.length);
  const shownSoFar = Math.max(quotes.length - withheldCount, 0);

  const modalData = {
    title: `We found ${shownSoFar} so far`,
    content: 'But there might be an even better deal. Want us to keep looking?',
    type: 'found-deals',
    buttons: [
      {
        index: 0,
        id: 'keep-searching',
        type: 'primary-pink',
        text: 'Yes, find me the best deal',
      },
      {
        index: 1,
        id: 'stop-searching',
        type: 'secondary-gray',
        text: 'No, Stop Searching',
      },
    ],
  };

  const quotesToRender = initialLoadDone ? sortedQuotes : quotesForAnimation;

  // Only show "No Quotes..." once the reveal flow is finished
  const showNoMatches =
    !fetching && initialLoadDone && quotesToRender.length === 0;

  let summaryText = '';
  if (showNoMatches) {
    summaryText = 'No Quotes Match Your Criteria';
  } else if (initialLoadDone) {
    summaryText = `${quotesToRender.length} Deal${quotesToRender.length > 1 ? 's' : ''} — Avg. price R${Math.round(avgPremium)}`;
  } else if (quotesToRender.length > 0) {
    summaryText = `${quotesToRender.length} Deal${quotesToRender.length > 1 ? 's' : ''} Found So Far`;
  }

  // Progress caps at "all except 3" until user says Yes, or at full set if user says No
  const progress = useMemo(() => {
    const total = quotes.length;

    const MIN = 0; // 5% baseline
    const PHASE1_MAX = 0.7; // cap before revealing top 3
    const PHASE2_MAX = 0.95; // cap while revealing top 3

    // While fetching, show a little progress so it never looks empty
    if (fetching) return MIN;
    if (total === 0) return 1;

    const withheld = Math.min(3, total);
    const nonTop3Target = Math.max(total - withheld, 1);

    // How many of the non-top-3 have we shown so far?
    const phase1Ratio = Math.min(visibleQuotes / nonTop3Target, 1);

    // If user said "No", we never reveal top 3 → finish at 100% when done
    if (hideTop3Permanently) {
      const ratio = Math.min(visibleQuotes / nonTop3Target, 1);
      const scaled = MIN + (1 - MIN) * ratio; // 5% → 100%
      return Math.min(scaled, initialLoadDone ? 1 : PHASE2_MAX);
    }

    // If we haven't been allowed to reveal top 3 yet, cap at PHASE1_MAX
    if (!allowFinalTile) {
      return Math.max(
        MIN,
        Math.min(MIN + (PHASE1_MAX - MIN) * phase1Ratio, PHASE1_MAX)
      );
    }

    // Phase 2: revealing top 3; compute progress over total quotes
    const totalTargetVisible = total;
    const phase2Ratio = Math.min(
      visibleQuotes / Math.max(totalTargetVisible, 1),
      1
    );

    // Map from PHASE1_MAX → PHASE2_MAX as the top 3 come in
    const scaledPhase2 = PHASE1_MAX + (PHASE2_MAX - PHASE1_MAX) * phase2Ratio;

    // Only hit 100% when initialLoadDone is true (best revealed / finished)
    return initialLoadDone ? 1 : Math.min(scaledPhase2, PHASE2_MAX);
  }, [
    fetching,
    visibleQuotes,
    quotes,
    initialLoadDone,
    hideTop3Permanently,
    allowFinalTile,
  ]);

  useEffect(() => {
    if (showIntroPanel && introPanelRef.current) {
      const elements = introPanelRef.current.querySelectorAll('.stagger-in');

      if (elements.length > 0) {
        gsap.set(elements, { opacity: 0, y: 30 });

        setTimeout(() => {
          gsap.to(elements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.1,
            overwrite: true,
          });
        }, 30);
      }
    }
  }, [showIntroPanel]);

  /* ------------------------------
     GSAP helpers
  -------------------------------*/

  const timelinesRef = useRef(new Map());

  const playEntrance = (el, { isBestDeal }) => {
    if (!el) return;
    const motionDiv = el.closest('[data-tile-motion]');

    const prev = timelinesRef.current.get(el.id);
    if (prev) prev.kill();

    const bounceSeq = (target) =>
      gsap
        .timeline()
        .set(
          target,
          { transformOrigin: '50% 50%', rotateZ: 0, scale: 0.75, opacity: 1 },
          0
        )
        .to(target, { scale: 1.06, duration: 0.4, ease: 'power3.out' }, 0)
        .to(
          target,
          { scale: 1.0, duration: 1.0, ease: 'elastic.out(1, 0.75)' },
          '>-0.02'
        );

    const tiltSeq = (target) =>
      gsap
        .timeline({ defaults: { ease: 'power2.inOut' } })
        .to(
          target,
          {
            duration: 1.2,
            rotateZ: -4,
            scale: 1.15,
            ease: 'power1.out',
          },
          0
        )
        .to(
          target,
          {
            duration: 1.5,
            rotateZ: 0,
            scale: 1.0,
            ease: 'elastic.out(1.5, 0.4)',
          },
          '>-0.02'
        );

    const tl = gsap.timeline();
    tl.add(bounceSeq(el), 0);
    if (isBestDeal && !hasSeenRevealRef.current) tl.add(tiltSeq(el), '>-0.90');

    if (isBestDeal) {
      tl.add(
        () => {
          const shouldReveal = !hasSeenRevealRef.current;
          if (shouldReveal) {
            setFireworksArmed(true);
            hasSeenRevealRef.current = true;
            sessionStorage.setItem('hippo_reveal_done', '1');
          }
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setBestDealContentReady(true);
              if (shouldReveal) setFireworksSignal((s) => s + 1);
              setInitialLoadDone(true);
            });
          });
        },
        hasSeenRevealRef.current ? '<' : '>-1.7'
      );
    }

    // drop the z-index back after the whole show
    tl.add(() => {
      if (motionDiv) gsap.set(motionDiv, { zIndex: 'auto' });
    });

    timelinesRef.current.set(el.id, tl);
    return tl;
  };

  const handleSelectQuote = useCallback(
    (e, { data }) => {
      markProductChosen('latestLifeInsuranceQuotes');
      navigate('/quoteSelection', {
        replace: true,
        state: { ...data, _product: product },
      });
    },
    [navigate, product]
  );

  const handleBuyOnline = useCallback(
    async (e, { data }) => {
      markProductChosen('latestLifeInsuranceQuotes');
      setBuyNowLoading(true);
      setBuyNowLoadingForQuotePk(data.QuotePK);
      const request = {
        QuotePK: data.QuotePK,
        Product: 'Life',
      };
      const response = await acceptQuoteBuyOnline(request);
      if (!response || !response.startUrl) {
        setBuyNowLoading(false);
        setBuyNowLoadingForQuotePk(null);
        return;
      }
      setBuyNowLoading(false);
      setBuyNowLoadingForQuotePk(null);
      const { startUrl } = response;
      window.open(startUrl, '_blank');
      navigate('/', { replace: true });
    },
    [navigate]
  );
  const { pathname } = useLocation();

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          mt: isMobile ? '0px' : '0.75rem',
          overflowX: 'clip',
        }}
      >
        {showIntroPanel && (
          <Box
            ref={introPanelRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: isTablet ? '24px' : isMobileOrTablet ? '0px' : '32px',
            }}
          >
            <img
              src={HippoAvatar}
              alt="Hippo Avatar"
              style={{
                // width: isMobile ? 80 : 80,
                // height: isMobile ? 80 : 80,
                width: '124px',
                height: '124px',
                objectFit: 'contain',
                margin: isTablet
                  ? '40px auto 0px'
                  : isMobile
                    ? '48px auto 0px'
                    : '0 auto 24px',
              }}
            />
            <Typography
              className="stagger-in"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: isMobileOrTablet ? '1.25rem' : '1.5rem',
                lineHeight: isMobileOrTablet ? '1.5rem' : '1.75rem',
                letterSpacing: '-0.03em',
                fontWeight: 400,
                color: '#162F3B',
                mb: isMobileOrTablet ? 2 : 4,
              }}
            >
              OK. We&rsquo;ve got what we need.
              <br />
              Ready to get your quotes?
            </Typography>

            <Button
              className="stagger-in"
              onClick={() => {
                setShowIntroPanel(false);
                if (!fetching) {
                  setTimeout(() => setIntroDelayDone(true), 2000);
                } else {
                  setIntroDelayDone(true);
                }
              }}
              sx={{
                backgroundColor: '#FF0074',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                borderRadius: '18px',
                padding: isMobileOrTablet ? '12px 24px' : '12px 32px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#e60073',
                },
              }}
            >
              Yes
            </Button>
          </Box>
        )}

        {!showIntroPanel && (
          <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
            <ResultHeaderComponent
              headerText={
                quotes.length === 0 ? 'Well, this is awkward...' : headerText
              }
              loadingText="Talking to insurers"
              initialLoadDone={initialLoadDone}
              sortOption={sortOption}
              setSortOption={setSortOption}
              progress={progress}
              sortOptions={sortOptions}
            />

            {introDelayDone && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '32px',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: '32px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {/* Left Panel – Filter (desktop only) */}
                    {!isMobileOrTablet && (
                      <Box sx={{ flex: '0 0 12.1875rem' }}>
                        <FilterPanelComponent
                          config={filterConfig}
                          filterPanelHeader="Life Cover"
                          filterPanelSubHeader={displayCoverValue}
                          setConfig={setFilterConfig}
                          onReset={handleReset}
                          onApply={handleApply}
                          onClose={() => setShowMobileFilterPanel(false)}
                          initialLoadDone={initialLoadDone}
                        />
                      </Box>
                    )}

                    {/* Mobile Filter Slide-Up */}
                    <AnimatePresence>
                      {isMobileOrTablet && showMobileFilterPanel && (
                        <>
                          <motion.div
                            onClick={() => setShowMobileFilterPanel(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(0, 0, 0, 0.4)',
                              backdropFilter: 'blur(4px)',
                              zIndex: 1400,
                            }}
                          />
                          <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            style={{
                              position: 'fixed',
                              overflow: 'hidden',
                              top: '250px',
                              left: 0,
                              width: '100%',
                              height: 'calc(100% - 250px)',
                              backgroundColor: '#FFFFFF',
                              borderTopLeftRadius: '16px',
                              borderTopRightRadius: '16px',
                              boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                              zIndex: 1500,
                              overflowY: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <MobileFilterPanelComponent
                              config={filterConfig}
                              filterPanelHeader="Life Cover"
                              filterPanelSubHeader={displayCoverValue}
                              setConfig={setFilterConfig}
                              onReset={handleReset}
                              onApply={handleApply}
                              onClose={() => setShowMobileFilterPanel(false)}
                              initialLoadDone={initialLoadDone}
                            />
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>

                    {/* Right Panel – Tiles */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        mb: isMobileOrTablet ? 'calc(48px + 24px)' : '32px',
                      }}
                    >
                      {fetching ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        ></Box>
                      ) : quotes.length === 0 ? (
                        <Box
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#1E354E',
                            textAlign: 'center',
                            py: 4,
                          }}
                        >
                          We searched all our partners, but couldn&rsquo;t find
                          a suitable offer for you right now.
                          <TakeMeHomeButton />
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              width: '100%',
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              fontSize: '1rem',
                              lineHeight: '1.75rem',
                              color: '#1E354E',
                              px: isMobileOrTablet ? '16px' : '0',
                            }}
                          >
                            {summaryText}
                          </Box>
                          <AnimatePresence mode="popLayout" initial={false}>
                            {quotesToRender.map((quote) => {
                              const isBestDeal = quote.bestDeal;
                              const tileId = `tile-${quote.QuotePK}`; // stable id for GSAP

                              return (
                                <motion.div
                                  data-tile-motion
                                  layout="position"
                                  key={quote.QuotePK}
                                  transition={{
                                    layout: {
                                      type: 'spring',
                                      stiffness: 520,
                                      damping: 42,
                                      mass: 0.9,
                                    },
                                    opacity: {
                                      duration: 0.28,
                                      ease: 'easeOut',
                                    },
                                    y: { duration: 0.28, ease: 'easeOut' },
                                  }}
                                  /* Enter AFTER layout pop finishes */
                                  initial={{ opacity: 0, y: -14 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: 18 }}
                                  style={{
                                    position: 'relative',
                                    willChange: 'transform, opacity',
                                    zIndex: isBestDeal ? 2500 : 'auto',
                                  }}
                                  onAnimationStart={() => {
                                    // run once per tile on first appearance only
                                    if (!scaledOnceRef.current.has(tileId)) {
                                      const el =
                                        document.getElementById(tileId);
                                      if (el) {
                                        playEntrance(el, { isBestDeal });
                                        scaledOnceRef.current.add(tileId);
                                      }
                                    }
                                  }}
                                  onAnimationComplete={() => {
                                    setVisibleQuotes((prev) => prev + 1);
                                  }}
                                >
                                  {/* simple wrapper — no logic change */}
                                  {/* Wrapper: keep the whole best-deal stack above side panels */}
                                  <Box
                                    id={tileId}
                                    sx={{
                                      position: 'relative',
                                      overflow: 'visible',
                                      zIndex: isBestDeal ? 2500 : 1,
                                    }}
                                  >
                                    {/* Fireworks BEHIND the ProviderTile (zIndex:1 vs card zIndex:2) */}
                                    {isBestDeal && fireworksArmed && (
                                      <FireworksFromTop
                                        playSignal={fireworksSignal}
                                        isMobile={isMobileOrTablet}
                                      />
                                    )}

                                    {/* Card sits above fireworks within this wrapper */}
                                    <Box
                                      sx={{ position: 'relative', zIndex: 2 }}
                                    >
                                      <ProviderTile
                                        category={category}
                                        product={product}
                                        data={quote}
                                        initialLoadDone={
                                          isBestDeal || initialLoadDone
                                        }
                                        bestDealContentReady={
                                          isBestDeal
                                            ? bestDealContentReady
                                            : true
                                        }
                                        isMobile={isMobileOrTablet}
                                        onSelectQuote={handleSelectQuote}
                                        onBuyOnline={handleBuyOnline}
                                        buyNowLoading={buyNowLoading}
                                        buyNowLoadingForQuotePk={
                                          buyNowLoadingForQuotePk
                                        }
                                        bestDealBuyOnline
                                        ref={isBestDeal ? bestDealRef : null}
                                      />
                                    </Box>
                                  </Box>
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Modal */}
            {showTeaseModal && modalData && (
              <PopupModalComponent
                open={showTeaseModal}
                onClose={() => {
                  setShowTeaseModal(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                data={modalData}
                scrollToTop
                onAction={handleModalButtonClick}
              />
            )}

            {/* Mobile Filter Button */}
            {isMobileOrTablet && (
              <Box
                onClick={() => setShowMobileFilterPanel(true)}
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '48px',
                  backgroundColor: '#C7CA00',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '24px',
                  zIndex: 9999,
                  cursor: 'pointer',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: '1.5rem',
                    letterSpacing: '-0.03em',
                    color: '#162F3B',
                  }}
                >
                  Filter
                </Box>
                <Box
                  component="span"
                  sx={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={FilterIcon}
                    alt="Unchecked"
                    style={{ width: '24px', height: '24px', display: 'block' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

/** Wrapper to inject mobile flags (same pattern as QuestionEngine) */
const ResultPageWithMobile = (props) => {
  const { isMobile, isTablet } = useMobileContext();
  return <ResultPage {...props} isMobile={isMobile} isTablet={isTablet} />;
};

export default withRouter(ResultPageWithMobile);
